import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link as LinkBase } from "react-router-dom";
import moment from "moment";

import { getStatusCode } from "@/helpers/utils";
import { CurrencyFormateText } from "@/components/CurrencyFormat";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import LearnMoreButtonBase from "@/components/muiCustom/buttons/LearnMoreButton";
import StatusChip from "@/components/muiCustom/chip/StatusChip";
import RpImageTextInline from "@/components/RpImageTextInline";
import TableServerBase from "@/components/tables/TableServer";
import useFetcher from "@/helpers/hooks/useFetcher";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import { CAMPAIGN_CONST, USER_ROLE } from "@/config/constants";
import { DIRECT_MAIL_CAMP_PROVIDER } from "@/features/campaigns/direct-mail/utils/directMailConstants";

type BrandDirectMailFilters = {
  pageSize: number;
  pageIndex: number;
  searchKey: string;
  sortBy: string;
  sortByDir: string;
};

type BrandDirectMailCampaign = {
  id?: string | number;
  campaign_name?: string;
  system_generated_campaign_id?: string;
  participant_template?: boolean;
  brand_owner_template?: boolean;
  parent_company_template?: boolean;
  campaign_status?: string | number;
  total_budget?: number;
  campaignBudget?: number;
  start_date?: string;
  schedule_date?: string;
  provider?: string;
  mailing_date_range?: string;
  [key: string]: any;
};

type BrandDirectMailReportResponse = {
  joinedCampaign?: BrandDirectMailCampaign[];
  pageCount?: number;
  recordsTotal?: number;
};

type BrandDirectMailCampaignListProps = {
  showFilter?: boolean;
  fromPage?: string;
  extraData?: {
    locationSysId?: string | number;
    [key: string]: any;
  };
  hideSearchLabel?: boolean;
  selectedBrand?: unknown;
  loggedInUser?: unknown;
};

type ContextUser = {
  id?: string | number;
  email?: string;
};

type ContextBrand = {
  id?: string | number;
};

const Link = LinkBase as React.ComponentType<any>;
const LearnMoreButton = LearnMoreButtonBase as React.ComponentType<any>;
const TableServer = TableServerBase as React.ComponentType<any>;

const BrandDirectMailCampaignList = ({
  showFilter = false,
  fromPage = "",
  extraData = {},
  hideSearchLabel = true,
}: BrandDirectMailCampaignListProps) => {
  const { loggedInUserRole, loggedInUser, selectedBrand } = useContext(
    GlobalContext
  ) as {
    loggedInUserRole?: string;
    loggedInUser?: ContextUser;
    selectedBrand?: ContextBrand;
  };
  const loader = useContext(LoaderContext) as (_isLoading: boolean) => void;
  const [filters, setFilters] = useState<BrandDirectMailFilters>({
    pageSize: 10,
    pageIndex: 0,
    searchKey: "",
    sortBy: "",
    sortByDir: "",
  });

  const reqData = useMemo(() => {
    return {
      id: selectedBrand?.id,
      userId: loggedInUser?.id,
      row: filters?.pageSize,
      page: filters?.pageIndex + 1,
      searchKey: filters?.searchKey || "",
      sortBy: filters?.sortBy || "",
      sortByDir: filters?.sortByDir || "",
      campaignCategory: "Postcard",
    };
  }, [selectedBrand?.id, loggedInUser?.id, filters]);

  const {
    data,
    isLoading: loading,
    isValidating,
  } = useFetcher<BrandDirectMailReportResponse>(
    reqData,
    campaignService.postcardReportListing,
    {
      revalidateOnFocus: false,
    }
  );

  const postcardReportList = data?.joinedCampaign || [];
  const pageCount = data?.pageCount || 0;
  const recordsTotal = data?.recordsTotal || 0;

  useEffect(() => {
    loader(loading || isValidating);
  }, [loading, loader, isValidating]);

  const fetchPostcardCampaignsReportData = useCallback(
    (data: Partial<BrandDirectMailFilters>) => {
      setFilters({
        pageSize: 10,
        pageIndex: 0,
        searchKey: "",
        sortBy: "",
        sortByDir: "",
        ...data,
      });
    },
    []
  );

  useEffect(() => {
    if (selectedBrand) {
      fetchPostcardCampaignsReportData({
        pageSize: 10,
        pageIndex: 0,
        searchKey: "",
      });
    }
  }, [selectedBrand, fetchPostcardCampaignsReportData]);

  const campaignList = useMemo(() => {
    if (
      [
        "sarah.dion@thetenspot.com",
        "angela.casullo@thetenspot.com",
        "jessica.scott@thetenspot.com",
      ].includes(loggedInUser?.email || "")
    ) {
      return postcardReportList?.filter(
        (c) => c.system_generated_campaign_id !== "FTDX7289"
      );
    }
    return postcardReportList;
  }, [postcardReportList, loggedInUser]);

  const columns = useMemo(
    () => [
      {
        Header: "Campaign Name",
        accessor: "campaign_name",
        id: "campaign_name",
        width: 215,
        sortType: "string",
        Cell: ({ row }: { row: { original: BrandDirectMailCampaign } }) => {
          const {
            original: {
              id,
              campaign_name,
              system_generated_campaign_id,
              participant_template,
              brand_owner_template,
              parent_company_template,
            },
          } = row;
          let searchQuery = "";

          if (
            fromPage &&
            fromPage === "location_report" &&
            extraData?.locationSysId
          ) {
            searchQuery = `?locationID=${extraData?.locationSysId}`;
          }

          const isTemplateExists =
            (loggedInUserRole === USER_ROLE.PARTICIPANT.NAME &&
              participant_template) ||
            ((loggedInUserRole === USER_ROLE.BRAND_OWNER.NAME ||
              loggedInUserRole === USER_ROLE.BRAND_USER.NAME) &&
              brand_owner_template) ||
            ((loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
              loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
              loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME) &&
              parent_company_template);

          const showLink = isTemplateExists;

          return showLink ? (
            <Link
              to={{
                pathname: `/dashboard/direct-mail-report/${system_generated_campaign_id}`,
                state: {
                  campaignId: id,
                  campaignSysId: system_generated_campaign_id,
                  from: "/dashboard",
                },
                search: searchQuery ? searchQuery : "",
              }}
              className="main-color link"
            >
              {campaign_name}
            </Link>
          ) : (
            campaign_name
          );
        },
      },
      {
        Header: "Status",
        accessor: (d) =>
          d.campaign_status === CAMPAIGN_CONST.STATUS.ACTIVE.NAME
            ? CAMPAIGN_CONST.STATUS.ACTIVE.TEXT
            : d.campaign_status,
        id: "campaign_status",
        width: 200,
        Cell: ({ row }) => {
          const {
            original: { campaign_status },
          } = row;

          const camp_status =
            campaign_status === CAMPAIGN_CONST.STATUS.ACTIVE.NAME
              ? CAMPAIGN_CONST.STATUS.ACTIVE.TEXT
              : campaign_status;

          return (
            <StatusChip
              label={camp_status}
              status={getStatusCode(camp_status)}
              showOnlyLabel
            />
          );
        },
      },
      {
        Header: "Cost",
        accessor: "total_budget",
        width: 200,
        Cell: ({ row }) => {
          const {
            original: { total_budget, campaignBudget },
          } = row;
          const cost = total_budget || campaignBudget || 0;
          return (
            <span className="fw-b">
              {cost ? <CurrencyFormateText value={cost} /> : "N/A"}
            </span>
          );
        },
      },
      {
        Header: "Scheduled Date",
        accessor: "schedule_date",
        id: "schedule_date",
        width: 260,
        Cell: ({ row }) => {
          const {
            original: {
              start_date,
              schedule_date,
              provider,
              mailing_date_range,
            },
          } = row;

          const getScheduleDates = (campaign) => {
            const start = moment(campaign?.schedule_date);
            const end = start.isValid() ? moment(start).add(4, "day") : null;
            if (
              campaign?.schedule_date &&
              start.isValid() &&
              provider !== DIRECT_MAIL_CAMP_PROVIDER?.RRD
            ) {
              return `${start.format("M/D/YYYY")} - ${end.format("M/D/YYYY")}`;
            } else if (provider === DIRECT_MAIL_CAMP_PROVIDER?.RRD) {
              if (
                !mailing_date_range ||
                mailing_date_range.includes("Invalid date")
              ) {
                return "N/A";
              }
              return mailing_date_range;
            }
            return "N/A";
          };

          const dateRangeText = getScheduleDates({ schedule_date });

          return (
            <RpImageTextInline
              svgIcon={
                start_date !== CAMPAIGN_CONST.TYPE.EVERGREEN
                  ? "campaign_calender"
                  : ""
              }
              imageProps={{ color: "primary" }}
              text={dateRangeText}
            />
          );
        },
      },
      {
        Header: () => <span className="">FAQ</span>,
        id: "faq",
        width: 200,
        canSort: false,
        disableSortBy: true,
        Cell: ({ row }) => {
          const { original } = row;
          return (
            <LearnMoreButton
              to={{
                pathname: `/dashboard/learn-more/direct-mail-campaign/${original.id}`,
                state: { campaignId: original.id, from: "/location-report" },
              }}
              className="mr-1 m-0 p-0"
              icon
            />
          );
        },
      },
    ],
    [loggedInUserRole, selectedBrand, fromPage, extraData]
  );

  return (
    <div className="location-list">
      <TableServer
        columns={columns}
        data={campaignList}
        showSearch={true}
        fetchData={fetchPostcardCampaignsReportData}
        isLoading={loading}
        pageCount={pageCount}
        showFilter={showFilter}
        useLayout={false}
        hideSearchLabel={hideSearchLabel}
        totalRecords={recordsTotal}
      />
    </div>
  );
};

export default BrandDirectMailCampaignList;
